// ============================================
// Reusable "Import Excel" + "Template" button bar for master pages.
// ============================================
import React, { useRef, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';

interface Props {
  /** Label shown on the import button (e.g. "Import Godowns") */
  label?: string;
  /** Path under /api that serves the .xlsx template (e.g. "/import/godowns/template") */
  templatePath: string;
  /** Axios-style upload function — takes a File, returns a promise with .data summary */
  uploader: (file: File) => Promise<{ data: any }>;
  /** Called after a successful import so the parent can refresh its list */
  onImported?: () => void | Promise<void>;
  /** Called with a status/error message for the parent to display */
  onMessage?: (msg: { type: 'success' | 'error'; text: string } | null) => void;
}

export const MasterImportBar: React.FC<Props> = ({ label = 'Import Excel', templatePath, uploader, onImported, onMessage }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const handleTemplate = async () => {
    try {
      const res = await fetch(`/api${templatePath}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('markfed_access_token') || ''}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || 'template.xlsx';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      onMessage?.({ type: 'error', text: 'Failed to download template' });
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setBusy(true);
    onMessage?.(null);
    try {
      const { data } = await uploader(file);
      const parts = [
        `${data.created || 0} created`,
        `${data.updated || 0} updated`,
        `${data.skipped || 0} skipped`,
      ];
      onMessage?.({ type: 'success', text: `Import done: ${parts.join(', ')}` });
      if (Array.isArray(data.warnings) && data.warnings.length) {
        // eslint-disable-next-line no-console
        console.warn('[import warnings]', data.warnings);
      }
      await onImported?.();
    } catch (err: any) {
      onMessage?.({ type: 'error', text: err?.response?.data?.message || 'Import failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input ref={inputRef} type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFile} className="hidden" />
      <button onClick={handleTemplate}
        className="px-3 py-2.5 text-sm font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 rounded-lg">
        Template
      </button>
      <button onClick={() => inputRef.current?.click()} disabled={busy}
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {busy ? 'Importing...' : label}
      </button>
    </div>
  );
};

export default MasterImportBar;
